import React, { useMemo, useState } from 'react';
import {
  Tabs,
  Button,
  Space,
  Typography,
  Card,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  App,
  Popconfirm,
  Select,
  Row,
  Col,
  Divider,
  DatePicker,
  Collapse,
  Empty,
  Spin,
  AutoComplete,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  CalendarOutlined,
  ArrowLeftOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { useResponsive } from '../hooks/useResponsive';
import { useCurrency } from '../hooks/useCurrency';
import { formatDateOnly } from '../utils/dateHelpers';
import {
  useGetDailyMenusByDateQuery,
  useCreateDailyMenuMutation,
  useUpdateDailyMenuMutation,
  useDeleteDailyMenuMutation,
  useCopyDailyMenuToDateMutation,
  useGetLatestDailyMenuQuery,
  useGetDishSuggestionsQuery,
  useToggleOptionAvailabilityMutation,
  useGetTemplatesQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useDeleteTemplateMutation,
} from '../services/dailyMenuApi';
import { useGetBoolSettingQuery } from '../services/settingsApi';
import { useGetActiveProductionCentersQuery } from '../services/productionCenterApi';
import { DailyMenuCountsPanel } from '../components/dailyMenu/DailyMenuCountsPanel';
import {
  filterDishSuggestions,
  indexDishSuggestions,
  normalizeDishName,
} from '../utils/dailyMenuGrouping';
import {
  MEAL_TYPE_OPTIONS as SHARED_MEAL_TYPE_OPTIONS,
  MEAL_TYPE_LABELS as SHARED_MEAL_TYPE_LABELS,
  MEAL_TYPE_COLORS as SHARED_MEAL_TYPE_COLORS,
} from '../components/dailyMenu/mealTypeConstants';
import type {
  DailyMenu,
  DailyMenuCourse,
  DailyMenuDishSuggestion,
  DailyMenuTemplate,
  MealType,
  CreateDailyMenuRequest,
  CreateDailyMenuCourseRequest,
  CreateDailyMenuOptionRequest,
  UpdateDailyMenuRequest,
  UpdateDailyMenuCourseRequest,
  UpdateDailyMenuOptionRequest,
  CreateDailyMenuTemplateRequest,
  UpdateDailyMenuTemplateRequest,
  UpdateDailyMenuTemplateCourseRequest,
} from '../types/dailyMenu';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;

const MEAL_TYPE_OPTIONS = SHARED_MEAL_TYPE_OPTIONS;
const MEAL_TYPE_LABELS = SHARED_MEAL_TYPE_LABELS;
const MEAL_TYPE_COLORS = SHARED_MEAL_TYPE_COLORS;

// =====================================================
// Course Editor Sub-component
// =====================================================

interface CourseEditorProps {
  courses: CourseFormData[];
  onChange: (courses: CourseFormData[]) => void;
  productionCenters: { id: string; name: string }[];
  isMobile: boolean;
  /** Platillos ya usados antes, para no volver a teclearlos. */
  dishSuggestions: DailyMenuDishSuggestion[];
  /** Precio base del menú: se suma al del platillo para mostrar lo que paga el comensal. */
  menuPrice: number;
}

interface OptionFormData {
  id?: string;
  name: string;
  description?: string;
  priceModifier: number;
  sortOrder: number;
  isAvailable: boolean;
  /**
   * Sólo del formulario (no se manda al backend): marca que el precio se
   * escribió a mano, para que el recetario no lo pise al reelegir el platillo.
   */
  priceTouched?: boolean;
}

interface CourseFormData {
  id?: string;
  name: string;
  isSelectable: boolean;
  fixedDescription?: string;
  sortOrder: number;
  productionCenterId?: string;
  options: OptionFormData[];
}

const CourseEditor: React.FC<CourseEditorProps> = ({
  courses,
  onChange,
  productionCenters,
  isMobile,
  dishSuggestions,
  menuPrice,
}) => {
  const { currencySymbol } = useCurrency();

  const addCourse = () => {
    onChange([
      ...courses,
      {
        name: '',
        isSelectable: true,
        sortOrder: courses.length,
        options: [],
      },
    ]);
  };

  const removeCourse = (index: number) => {
    const updated = courses.filter((_, i) => i !== index);
    onChange(updated.map((c, i) => ({ ...c, sortOrder: i })));
  };

  const updateCourse = (index: number, field: string, value: any) => {
    const updated = [...courses];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const moveCourse = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === courses.length - 1)
    )
      return;
    const updated = [...courses];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]];
    onChange(updated.map((c, i) => ({ ...c, sortOrder: i })));
  };

  const addOption = (courseIndex: number) => {
    const updated = [...courses];
    updated[courseIndex] = {
      ...updated[courseIndex],
      options: [
        ...updated[courseIndex].options,
        {
          name: '',
          priceModifier: 0,
          sortOrder: updated[courseIndex].options.length,
          isAvailable: true,
        },
      ],
    };
    onChange(updated);
  };

  const removeOption = (courseIndex: number, optionIndex: number) => {
    const updated = [...courses];
    updated[courseIndex] = {
      ...updated[courseIndex],
      options: updated[courseIndex].options
        .filter((_, i) => i !== optionIndex)
        .map((o, i) => ({ ...o, sortOrder: i })),
    };
    onChange(updated);
  };

  const updateOption = (courseIndex: number, optionIndex: number, field: string, value: any) => {
    const updated = [...courses];
    const options = [...updated[courseIndex].options];
    options[optionIndex] = { ...options[optionIndex], [field]: value };
    // Un precio escrito a mano manda sobre el del recetario: sin esta marca,
    // volver a elegir el platillo de la lista lo pisaría en silencio.
    if (field === 'priceModifier') {
      options[optionIndex].priceTouched = true;
    }
    updated[courseIndex] = { ...updated[courseIndex], options };
    onChange(updated);
  };

  /**
   * Al elegir del recetario se escriben nombre y precio de un solo golpe: dos
   * llamadas seguidas a updateOption partirían del mismo estado y la segunda
   * pisaría a la primera.
   */
  const applySuggestion = (
    courseIndex: number,
    optionIndex: number,
    suggestion: DailyMenuDishSuggestion
  ) => {
    const updated = [...courses];
    const options = [...updated[courseIndex].options];
    const current = options[optionIndex];
    options[optionIndex] = {
      ...current,
      name: suggestion.name,
      // El precio del recetario es de hasta 180 días atrás: sólo se pone si
      // el renglón no trae uno tecleado a propósito.
      priceModifier: current.priceTouched ? current.priceModifier : suggestion.priceModifier,
    };
    updated[courseIndex] = { ...updated[courseIndex], options };
    onChange(updated);
  };

  // El recetario se normaliza una vez, no por renglón y por tecla.
  const indexedSuggestions = useMemo(
    () => indexDishSuggestions(dishSuggestions),
    [dishSuggestions]
  );

  // Llaves de los platillos ya capturados, por tiempo: se calculan una vez por
  // render en vez de una vez por renglón.
  const optionKeysByCourse = useMemo(
    () => courses.map((c) => c.options.map((o) => normalizeDishName(o.name))),
    [courses]
  );

  /** Sugerencias para un renglón: las del tiempo primero y sin repetir nombre. */
  const suggestionOptionsFor = (course: CourseFormData, courseIdx: number, optIdx: number) => {
    const courseKeys = optionKeysByCourse[courseIdx] ?? [];
    const usedKeys = new Set(
      courseKeys.filter((key, i) => key !== '' && i !== optIdx)
    );

    return filterDishSuggestions({
      indexed: indexedSuggestions,
      courseName: course.name,
      query: course.options[optIdx]?.name,
      usedKeys,
    })
      .map((s) => ({
        value: s.name,
        suggestion: s,
        label: (
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <span>{s.name}</span>
            <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
              {currencySymbol}
              {s.priceModifier.toLocaleString('es-MX', { maximumFractionDigits: 2 })} ·{' '}
              {s.timesUsed} {s.timesUsed === 1 ? 'vez' : 'veces'}
            </Text>
          </div>
        ),
      }));
  };

  return (
    <div>
      {courses.map((course, courseIdx) => (
        <Card
          key={courseIdx}
          size="small"
          style={{ marginBottom: 12 }}
          title={
            <Space>
              <Text strong>Tiempo {courseIdx + 1}</Text>
              <Button
                type="text"
                size="small"
                icon={<ArrowUpOutlined />}
                disabled={courseIdx === 0}
                onClick={() => moveCourse(courseIdx, 'up')}
              />
              <Button
                type="text"
                size="small"
                icon={<ArrowDownOutlined />}
                disabled={courseIdx === courses.length - 1}
                onClick={() => moveCourse(courseIdx, 'down')}
              />
            </Space>
          }
          extra={
            <Button
              type="text"
              danger
              size="small"
              icon={<MinusCircleOutlined />}
              onClick={() => removeCourse(courseIdx)}
            >
              {!isMobile && 'Quitar'}
            </Button>
          }
        >
          <Row gutter={[12, 8]}>
            <Col xs={24} sm={12}>
              <Input
                placeholder="Nombre del tiempo (ej: Entrada, Plato fuerte)"
                value={course.name}
                onChange={(e) => updateCourse(courseIdx, 'name', e.target.value)}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Space>
                <Text type="secondary" style={{ fontSize: 12 }}>Seleccionable:</Text>
                <Switch
                  checked={course.isSelectable}
                  onChange={(v) => updateCourse(courseIdx, 'isSelectable', v)}
                  size="small"
                />
              </Space>
            </Col>
            <Col xs={12} sm={6}>
              {productionCenters.length > 0 && (
                <Select
                  placeholder="Centro prod."
                  allowClear
                  size="small"
                  style={{ width: '100%' }}
                  value={course.productionCenterId}
                  onChange={(v) => updateCourse(courseIdx, 'productionCenterId', v)}
                  options={productionCenters.map((pc) => ({ label: pc.name, value: pc.id }))}
                />
              )}
            </Col>
          </Row>

          {!course.isSelectable && (
            <div style={{ marginTop: 8 }}>
              <Input
                placeholder="Descripcion fija (ej: Sopa del dia)"
                value={course.fixedDescription || ''}
                onChange={(e) => updateCourse(courseIdx, 'fixedDescription', e.target.value)}
              />
            </div>
          )}

          {course.isSelectable && (
            <div style={{ marginTop: 12 }}>
              <Text type="secondary" style={{ fontSize: 12, marginBottom: 4, display: 'block' }}>
                Opciones:
              </Text>
              {course.options.map((option, optIdx) => (
                <Row key={optIdx} gutter={[8, 4]} style={{ marginBottom: 4 }} align="middle">
                  <Col xs={24} sm={8}>
                    <AutoComplete
                      size="small"
                      style={{ width: '100%' }}
                      placeholder="Nombre del platillo"
                      value={option.name}
                      // El filtrado va por nuestra cuenta (sin acentos, por tiempo),
                      // así que AntD no debe volver a filtrar la lista.
                      filterOption={false}
                      options={suggestionOptionsFor(course, courseIdx, optIdx)}
                      onChange={(v) => updateOption(courseIdx, optIdx, 'name', v ?? '')}
                      onSelect={(_v, opt: any) => applySuggestion(courseIdx, optIdx, opt.suggestion)}
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Input
                      placeholder="Descripcion"
                      size="small"
                      value={option.description || ''}
                      onChange={(e) => updateOption(courseIdx, optIdx, 'description', e.target.value)}
                    />
                  </Col>
                  <Col xs={6} sm={4}>
                    {/*
                      Sin Tooltip a propósito: en la tablet, tocar el campo para
                      editarlo dispararía el globo encima del renglón de arriba.
                      El placeholder ya dice si es precio o incremento.
                    */}
                    <InputNumber
                      placeholder={menuPrice > 0 ? `+/- ${currencySymbol}` : `Precio ${currencySymbol}`}
                      size="small"
                      step={0.5}
                      style={{ width: '100%' }}
                      value={option.priceModifier}
                      onChange={(v) => updateOption(courseIdx, optIdx, 'priceModifier', v ?? 0)}
                    />
                  </Col>
                  <Col xs={3} sm={3}>
                    <Switch
                      checked={option.isAvailable}
                      onChange={(v) => updateOption(courseIdx, optIdx, 'isAvailable', v)}
                      size="small"
                      checkedChildren="Disp"
                      unCheckedChildren="No"
                    />
                  </Col>
                  <Col xs={3} sm={3}>
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<MinusCircleOutlined />}
                      onClick={() => removeOption(courseIdx, optIdx)}
                    />
                  </Col>
                </Row>
              ))}
              <Button
                type="dashed"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => addOption(courseIdx)}
                style={{ marginTop: 4 }}
              >
                Agregar opcion
              </Button>
            </div>
          )}
        </Card>
      ))}
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={addCourse}
        block
      >
        Agregar Tiempo
      </Button>
    </div>
  );
};

// =====================================================
// Template Course Editor (simpler, no options)
// =====================================================

interface TemplateCourseFormData {
  id?: string;
  name: string;
  isSelectable: boolean;
  fixedDescription?: string;
  sortOrder: number;
  productionCenterId?: string;
}

interface TemplateCourseEditorProps {
  courses: TemplateCourseFormData[];
  onChange: (courses: TemplateCourseFormData[]) => void;
  productionCenters: { id: string; name: string }[];
  isMobile: boolean;
}

const TemplateCourseEditor: React.FC<TemplateCourseEditorProps> = ({
  courses,
  onChange,
  productionCenters,
  isMobile,
}) => {
  const addCourse = () => {
    onChange([
      ...courses,
      { name: '', isSelectable: true, sortOrder: courses.length },
    ]);
  };

  const removeCourse = (index: number) => {
    onChange(
      courses.filter((_, i) => i !== index).map((c, i) => ({ ...c, sortOrder: i }))
    );
  };

  const updateCourse = (index: number, field: string, value: any) => {
    const updated = [...courses];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  return (
    <div>
      {courses.map((course, idx) => (
        <Row key={idx} gutter={[8, 8]} style={{ marginBottom: 8 }} align="middle">
          <Col xs={24} sm={8}>
            <Input
              placeholder="Nombre del tiempo"
              size="small"
              value={course.name}
              onChange={(e) => updateCourse(idx, 'name', e.target.value)}
            />
          </Col>
          <Col xs={8} sm={4}>
            <Space size={4}>
              <Text type="secondary" style={{ fontSize: 11 }}>Selecc:</Text>
              <Switch
                checked={course.isSelectable}
                onChange={(v) => updateCourse(idx, 'isSelectable', v)}
                size="small"
              />
            </Space>
          </Col>
          <Col xs={8} sm={6}>
            {productionCenters.length > 0 && (
              <Select
                placeholder="Centro"
                allowClear
                size="small"
                style={{ width: '100%' }}
                value={course.productionCenterId}
                onChange={(v) => updateCourse(idx, 'productionCenterId', v)}
                options={productionCenters.map((pc) => ({ label: pc.name, value: pc.id }))}
              />
            )}
          </Col>
          <Col xs={8} sm={6}>
            <Button
              type="text"
              danger
              size="small"
              icon={<MinusCircleOutlined />}
              onClick={() => removeCourse(idx)}
            >
              {!isMobile && 'Quitar'}
            </Button>
          </Col>
        </Row>
      ))}
      <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={addCourse} block>
        Agregar Tiempo
      </Button>
    </div>
  );
};

// =====================================================
// Main Component
// =====================================================

const DailyMenuAdmin: React.FC = () => {
  // App.useApp() y no el import estatico: bajo React 19 el estatico no dibuja
  const { message } = App.useApp();
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const { currencySymbol } = useCurrency();

  // State
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [activeTab, setActiveTab] = useState<string>('menus');
  const [isMenuModalOpen, setIsMenuModalOpen] = useState(false);
  const [editingMenu, setEditingMenu] = useState<DailyMenu | null>(null);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [copyingMenuId, setCopyingMenuId] = useState<string | null>(null);
  const [copyTargetDate, setCopyTargetDate] = useState<Dayjs | null>(null);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<DailyMenuTemplate | null>(null);

  // Menu form state (managed outside antd Form for course editor)
  const [menuForm] = Form.useForm();
  const [menuCourses, setMenuCourses] = useState<CourseFormData[]>([]);
  const [templateForm] = Form.useForm();
  const [templateCourses, setTemplateCourses] = useState<TemplateCourseFormData[]>([]);

  // Queries
  const dateStr = selectedDate.format('YYYY-MM-DD');
  const { data: menus = [], isLoading: loadingMenus } = useGetDailyMenusByDateQuery({ date: dateStr });
  const { data: templates = [], isLoading: loadingTemplates } = useGetTemplatesQuery();
  const { data: activeProductionCenters = [] } = useGetActiveProductionCentersQuery();
  const { data: menuDelDiaSetting } = useGetBoolSettingQuery('MenuDelDiaActivo');

  // Latest menu query (for clone button)
  const { data: latestMenu, isError: latestMenuError } = useGetLatestDailyMenuQuery();

  // Recetario de platillos ya usados, para el buscador del modal de captura.
  const { data: dishSuggestions = [] } = useGetDishSuggestionsQuery();

  // Mutations
  const [createDailyMenu, { isLoading: creating }] = useCreateDailyMenuMutation();
  const [updateDailyMenu, { isLoading: updating }] = useUpdateDailyMenuMutation();
  const [deleteDailyMenu] = useDeleteDailyMenuMutation();
  const [copyDailyMenuToDate, { isLoading: copying }] = useCopyDailyMenuToDateMutation();
  const [toggleOptionAvailability] = useToggleOptionAvailabilityMutation();
  const [createTemplate, { isLoading: creatingTemplate }] = useCreateTemplateMutation();
  const [updateTemplate, { isLoading: updatingTemplate }] = useUpdateTemplateMutation();
  const [deleteTemplate] = useDeleteTemplateMutation();

  const pcOptions = activeProductionCenters.map((pc) => ({ id: pc.id, name: pc.name }));

  // El precio del menú vive en el Form; se observa para poder avisar del
  // cobro en cero y para mostrar el precio efectivo de cada platillo.
  const watchedMenuPrice = Form.useWatch<number | undefined>('price', menuForm) ?? 0;

  /**
   * Un menú se puede quedar en cero cuando ni el menú ni la opción más barata
   * de cada tiempo cuestan algo — el comensal se cobraría en $0 sin que nadie
   * lo note hasta el corte. Es aviso, no candado: el precio puede estar a
   * medio capturar y no queremos estorbar.
   *
   * Un menú de puros tiempos fijos es el caso más peligroso, no el más
   * inocente: no hay ninguna opción con precio y `DailyMenuPricingValidation`
   * lo deja cerrar sin chistar, así que cobra cero a todos. Por eso el corte
   * es "no hay tiempos", no "no hay tiempos con precio".
   */
  const cheapestComboIsFree = useMemo(() => {
    if (menuCourses.length === 0) return false;

    // Un menú a medio armar (tiempo seleccionable todavía sin opciones) ni
    // siquiera pasa la validación de guardado, así que avisar ahí sería ruido:
    // saltaría al instante de elegir la plantilla, antes de teclear nada.
    if (menuCourses.some((c) => c.isSelectable && c.options.length === 0)) return false;

    const cheapest = menuCourses
      .filter((c) => c.isSelectable)
      .reduce((sum, c) => sum + Math.min(...c.options.map((o) => o.priceModifier || 0)), 0);

    return watchedMenuPrice + cheapest <= 0;
  }, [menuCourses, watchedMenuPrice]);

  // Group menus by MealType
  const menusByMealType = menus.reduce<Record<MealType, DailyMenu[]>>((acc, menu) => {
    if (!acc[menu.mealType]) acc[menu.mealType] = [];
    acc[menu.mealType].push(menu);
    return acc;
  }, {} as Record<MealType, DailyMenu[]>);

  // =====================================================
  // Menu handlers
  // =====================================================

  const mapCoursesToFormData = (courses: DailyMenuCourse[]): CourseFormData[] =>
    courses.map((c) => ({
      id: c.id,
      name: c.name,
      isSelectable: c.isSelectable,
      fixedDescription: c.fixedDescription,
      sortOrder: c.sortOrder,
      productionCenterId: c.productionCenterId,
      options: c.options.map((o) => ({
        id: o.id,
        name: o.name,
        description: o.description,
        priceModifier: o.priceModifier,
        sortOrder: o.sortOrder,
        isAvailable: o.isAvailable,
      })),
    }));

  const handleAddMenu = () => {
    setEditingMenu(null);
    menuForm.resetFields();
    menuForm.setFieldsValue({
      mealType: 'Lunch',
      price: 0,
      sortOrder: menus.length,
      isActive: true,
    });
    setMenuCourses([]);
    setIsMenuModalOpen(true);
  };

  const handleEditMenu = (menu: DailyMenu) => {
    setEditingMenu(menu);
    menuForm.setFieldsValue({
      name: menu.name,
      description: menu.description,
      price: menu.price,
      mealType: menu.mealType,
      sortOrder: menu.sortOrder,
      isActive: menu.isActive,
    });
    setMenuCourses(mapCoursesToFormData(menu.courses));
    setIsMenuModalOpen(true);
  };

  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find((t) => t.id === templateId);
    if (!template) return;
    menuForm.setFieldsValue({
      name: template.name,
      description: template.description,
      price: template.defaultPrice,
      mealType: template.mealType,
    });
    setMenuCourses(
      template.courses.map((c) => ({
        name: c.name,
        isSelectable: c.isSelectable,
        fixedDescription: c.fixedDescription,
        sortOrder: c.sortOrder,
        productionCenterId: c.productionCenterId,
        options: [],
      }))
    );
  };

  const handleMenuModalOk = async () => {
    try {
      const values = await menuForm.validateFields();

      // Validate courses
      if (menuCourses.length === 0) {
        message.warning('Agrega al menos un tiempo al menu');
        return;
      }

      for (const course of menuCourses) {
        if (!course.name.trim()) {
          message.warning('Todos los tiempos deben tener un nombre');
          return;
        }
        if (course.isSelectable && course.options.length === 0) {
          message.warning(`El tiempo "${course.name}" es seleccionable pero no tiene opciones`);
          return;
        }
        for (const option of course.options) {
          if (!option.name.trim()) {
            message.warning(`Todas las opciones del tiempo "${course.name}" deben tener nombre`);
            return;
          }
        }
      }

      if (editingMenu) {
        const updateReq: UpdateDailyMenuRequest & { id: string } = {
          id: editingMenu.id,
          name: values.name,
          description: values.description,
          price: values.price,
          isActive: values.isActive ?? true,
          sortOrder: values.sortOrder ?? 0,
          courses: menuCourses.map((c): UpdateDailyMenuCourseRequest => ({
            id: c.id,
            name: c.name,
            isSelectable: c.isSelectable,
            fixedDescription: c.fixedDescription,
            sortOrder: c.sortOrder,
            productionCenterId: c.productionCenterId,
            options: c.options.map((o): UpdateDailyMenuOptionRequest => ({
              id: o.id,
              name: o.name,
              description: o.description,
              priceModifier: o.priceModifier,
              sortOrder: o.sortOrder,
              isAvailable: o.isAvailable,
            })),
          })),
        };
        await updateDailyMenu(updateReq).unwrap();
        message.success('Menu actualizado correctamente');
      } else {
        const createReq: CreateDailyMenuRequest = {
          date: dateStr,
          mealType: values.mealType,
          name: values.name,
          description: values.description,
          price: values.price,
          sortOrder: values.sortOrder ?? 0,
          templateId: values.templateId,
          courses: menuCourses.map((c): CreateDailyMenuCourseRequest => ({
            name: c.name,
            isSelectable: c.isSelectable,
            fixedDescription: c.fixedDescription,
            sortOrder: c.sortOrder,
            productionCenterId: c.productionCenterId,
            options: c.options.map((o): CreateDailyMenuOptionRequest => ({
              name: o.name,
              description: o.description,
              priceModifier: o.priceModifier,
              sortOrder: o.sortOrder,
            })),
          })),
        };
        await createDailyMenu(createReq).unwrap();
        message.success('Menu creado correctamente');
      }

      setIsMenuModalOpen(false);
      menuForm.resetFields();
      setMenuCourses([]);
    } catch (error: any) {
      console.error('Error saving daily menu:', error);
      const errorMsg = error?.data?.message || 'Error al guardar el menu del dia';
      message.error(errorMsg, 5);
    }
  };

  const handleDeleteMenu = async (id: string) => {
    try {
      await deleteDailyMenu(id).unwrap();
      message.success('Menu eliminado correctamente');
    } catch (error: any) {
      message.error(error?.data?.message || 'Error al eliminar el menu');
    }
  };

  const handleCopyMenu = (menuId: string) => {
    setCopyingMenuId(menuId);
    setCopyTargetDate(null);
    setIsCopyModalOpen(true);
  };

  const handleCopyConfirm = async () => {
    if (!copyingMenuId || !copyTargetDate) return;
    try {
      await copyDailyMenuToDate({
        id: copyingMenuId,
        targetDate: copyTargetDate.format('YYYY-MM-DD'),
      }).unwrap();
      message.success('Menu copiado correctamente');
      setIsCopyModalOpen(false);
    } catch (error: any) {
      message.error(error?.data?.message || 'Error al copiar el menu');
    }
  };

  const handleCloneLatestMenu = async () => {
    if (!latestMenu) {
      message.error('No hay menus del dia configurados previamente para clonar');
      return;
    }
    try {
      await copyDailyMenuToDate({
        id: latestMenu.id,
        targetDate: selectedDate.format('YYYY-MM-DD'),
      }).unwrap();
      message.success(`Menu "${latestMenu.name}" clonado a ${selectedDate.format('DD/MM/YYYY')}`);
    } catch (error: any) {
      message.error(error?.data?.message || 'Error al clonar el menu');
    }
  };

  const handleToggleOptionAvailability = async (optionId: string, isAvailable: boolean) => {
    try {
      await toggleOptionAvailability({ optionId, isAvailable }).unwrap();
    } catch (error: any) {
      message.error(error?.data?.message || 'Error al cambiar disponibilidad');
    }
  };

  // =====================================================
  // Template handlers
  // =====================================================

  const handleAddTemplate = () => {
    setEditingTemplate(null);
    templateForm.resetFields();
    templateForm.setFieldsValue({ mealType: 'Lunch', defaultPrice: 0, isActive: true });
    setTemplateCourses([]);
    setIsTemplateModalOpen(true);
  };

  const handleEditTemplate = (template: DailyMenuTemplate) => {
    setEditingTemplate(template);
    templateForm.setFieldsValue({
      name: template.name,
      description: template.description,
      mealType: template.mealType,
      defaultPrice: template.defaultPrice,
      isActive: template.isActive,
    });
    setTemplateCourses(
      template.courses.map((c) => ({
        id: c.id,
        name: c.name,
        isSelectable: c.isSelectable,
        fixedDescription: c.fixedDescription,
        sortOrder: c.sortOrder,
        productionCenterId: c.productionCenterId,
      }))
    );
    setIsTemplateModalOpen(true);
  };

  const handleDeleteTemplate = async (id: string) => {
    try {
      await deleteTemplate(id).unwrap();
      message.success('Plantilla eliminada correctamente');
    } catch (error: any) {
      message.error(error?.data?.message || 'Error al eliminar la plantilla');
    }
  };

  const handleTemplateModalOk = async () => {
    try {
      const values = await templateForm.validateFields();

      if (templateCourses.length === 0) {
        message.warning('Agrega al menos un tiempo a la plantilla');
        return;
      }

      for (const course of templateCourses) {
        if (!course.name.trim()) {
          message.warning('Todos los tiempos deben tener un nombre');
          return;
        }
      }

      if (editingTemplate) {
        const req: UpdateDailyMenuTemplateRequest & { id: string } = {
          id: editingTemplate.id,
          name: values.name,
          description: values.description,
          defaultPrice: values.defaultPrice,
          isActive: values.isActive ?? true,
          courses: templateCourses.map((c): UpdateDailyMenuTemplateCourseRequest => ({
            id: c.id,
            name: c.name,
            isSelectable: c.isSelectable,
            fixedDescription: c.fixedDescription,
            sortOrder: c.sortOrder,
            productionCenterId: c.productionCenterId,
          })),
        };
        await updateTemplate(req).unwrap();
        message.success('Plantilla actualizada correctamente');
      } else {
        const req: CreateDailyMenuTemplateRequest = {
          mealType: values.mealType,
          name: values.name,
          description: values.description,
          defaultPrice: values.defaultPrice,
          courses: templateCourses.map((c) => ({
            name: c.name,
            isSelectable: c.isSelectable,
            fixedDescription: c.fixedDescription,
            sortOrder: c.sortOrder,
            productionCenterId: c.productionCenterId,
          })),
        };
        await createTemplate(req).unwrap();
        message.success('Plantilla creada correctamente');
      }

      setIsTemplateModalOpen(false);
      templateForm.resetFields();
      setTemplateCourses([]);
    } catch (error: any) {
      console.error('Error saving template:', error);
      message.error(error?.data?.message || 'Error al guardar la plantilla');
    }
  };

  // =====================================================
  // Render helpers
  // =====================================================

  const renderMenuCard = (menu: DailyMenu) => (
    <Card
      key={menu.id}
      size="small"
      style={{ marginBottom: 12 }}
      title={
        <Space>
          <Text strong>{menu.name}</Text>
          <Tag color={menu.isActive ? 'green' : 'red'}>
            {menu.isActive ? 'Activo' : 'Inactivo'}
          </Tag>
        </Space>
      }
      extra={
        <Space size="small">
          <Text strong>{currencySymbol} {menu.price.toFixed(2)}</Text>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditMenu(menu)}>
            {!isMobile && 'Editar'}
          </Button>
          <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => handleCopyMenu(menu.id)}>
            {!isMobile && 'Copiar'}
          </Button>
          <Popconfirm
            title="Eliminar menu del dia?"
            description="Esta accion no se puede deshacer"
            onConfirm={() => handleDeleteMenu(menu.id)}
            okText="Eliminar"
            cancelText="Cancelar"
          >
            <Button type="link" danger size="small" icon={<DeleteOutlined />}>
              {!isMobile && 'Eliminar'}
            </Button>
          </Popconfirm>
        </Space>
      }
    >
      {menu.description && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          {menu.description}
        </Text>
      )}

      {menu.courses.length === 0 ? (
        <Text type="secondary">Sin tiempos configurados</Text>
      ) : (
        <Collapse size="small" ghost>
          {menu.courses
            .slice()
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((course) => (
              <Panel
                key={course.id}
                header={
                  <Space>
                    <Text>{course.name}</Text>
                    {course.isSelectable ? (
                      <Tag color="blue">{course.options.length} opciones</Tag>
                    ) : (
                      <Tag color="default">Fijo</Tag>
                    )}
                    {course.productionCenterName && (
                      <Tag>{course.productionCenterName}</Tag>
                    )}
                  </Space>
                }
              >
                {course.isSelectable ? (
                  course.options.length === 0 ? (
                    <Text type="secondary">Sin opciones</Text>
                  ) : (
                    <div>
                      {course.options
                        .slice()
                        .sort((a, b) => a.sortOrder - b.sortOrder)
                        .map((option) => (
                          <div
                            key={option.id}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 0',
                              borderBottom: '1px solid var(--border-color)',
                            }}
                          >
                            <Space>
                              <Text>{option.name}</Text>
                              {option.description && (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  ({option.description})
                                </Text>
                              )}
                              {option.priceModifier !== 0 && (
                                <Tag color={option.priceModifier > 0 ? 'orange' : 'green'}>
                                  {option.priceModifier > 0 ? '+' : ''}{currencySymbol} {option.priceModifier.toFixed(2)}
                                </Tag>
                              )}
                            </Space>
                            <Switch
                              checked={option.isAvailable}
                              onChange={(checked) =>
                                handleToggleOptionAvailability(option.id, checked)
                              }
                              size="small"
                              checkedChildren="Disp"
                              unCheckedChildren="No"
                            />
                          </div>
                        ))}
                    </div>
                  )
                ) : (
                  <Text type="secondary">{course.fixedDescription || 'Sin descripcion'}</Text>
                )}
              </Panel>
            ))}
        </Collapse>
      )}

      <Collapse size="small" ghost style={{ marginTop: 8 }}>
        <Panel key="counts" header={<Text strong>Comandas del día</Text>}>
          <DailyMenuCountsPanel menuId={menu.id} bordered={false} showHeader={false} />
        </Panel>
      </Collapse>
    </Card>
  );

  const renderMenusTab = () => (
    <div>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          gap: 16,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <DatePicker
          value={selectedDate}
          onChange={(date) => date && setSelectedDate(date)}
          format="DD/MM/YYYY"
          allowClear={false}
          size={isMobile ? 'middle' : 'large'}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddMenu}
          size={isMobile ? 'middle' : 'large'}
        >
          Crear Menu
        </Button>
        <Button
          icon={<CopyOutlined />}
          onClick={handleCloneLatestMenu}
          size={isMobile ? 'middle' : 'large'}
          loading={copying}
          disabled={!latestMenu || latestMenuError}
          title={latestMenu ? `Clonar: ${latestMenu.name} (${formatDateOnly(latestMenu.date, 'DD/MM/YYYY')})` : 'No hay menus previos'}
        >
          {!isMobile && 'Clonar Último Menu'}
        </Button>
      </div>

      {loadingMenus ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      ) : menus.length === 0 ? (
        <Empty
          description={`No hay menus del dia configurados para ${selectedDate.format('DD/MM/YYYY')}`}
        >
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddMenu}>
            Crear Menu del Dia
          </Button>
        </Empty>
      ) : (
        (['Breakfast', 'Lunch', 'Dinner'] as MealType[]).map((mealType) => {
          const mealMenus = menusByMealType[mealType];
          if (!mealMenus || mealMenus.length === 0) return null;
          return (
            <div key={mealType} style={{ marginBottom: 24 }}>
              <Title level={4}>
                <Tag color={MEAL_TYPE_COLORS[mealType]}>{MEAL_TYPE_LABELS[mealType]}</Tag>
              </Title>
              {mealMenus.map((menu) => renderMenuCard(menu))}
            </div>
          );
        })
      )}
    </div>
  );

  const renderTemplatesTab = () => (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddTemplate}
          size={isMobile ? 'middle' : 'large'}
        >
          Crear Plantilla
        </Button>
      </div>

      {loadingTemplates ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      ) : templates.length === 0 ? (
        <Empty description="No hay plantillas de menu creadas">
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddTemplate}>
            Crear Plantilla
          </Button>
        </Empty>
      ) : (
        <Row gutter={[16, 16]}>
          {templates.map((template) => (
            <Col xs={24} sm={12} md={8} key={template.id}>
              <Card
                size="small"
                title={template.name}
                extra={
                  <Space size="small">
                    <Tag color={MEAL_TYPE_COLORS[template.mealType]}>
                      {MEAL_TYPE_LABELS[template.mealType]}
                    </Tag>
                    <Tag color={template.isActive ? 'green' : 'red'}>
                      {template.isActive ? 'Activa' : 'Inactiva'}
                    </Tag>
                  </Space>
                }
                actions={[
                  <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => handleEditTemplate(template)}
                    size="small"
                  >
                    Editar
                  </Button>,
                  <Popconfirm
                    title="Eliminar plantilla?"
                    onConfirm={() => handleDeleteTemplate(template.id)}
                    okText="Eliminar"
                    cancelText="Cancelar"
                  >
                    <Button type="link" danger icon={<DeleteOutlined />} size="small">
                      Eliminar
                    </Button>
                  </Popconfirm>,
                ]}
              >
                {template.description && (
                  <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                    {template.description}
                  </Text>
                )}
                <Text>
                  Precio base: <Text strong>{currencySymbol} {template.defaultPrice.toFixed(2)}</Text>
                </Text>
                <br />
                <Text type="secondary">
                  {template.courses.length} tiempo(s):{' '}
                  {template.courses.map((c) => c.name).join(', ')}
                </Text>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );

  const tabItems = [
    {
      key: 'menus',
      label: (
        <span>
          <CalendarOutlined />
          {!isMobile && ' Menus del Dia'}
        </span>
      ),
      children: renderMenusTab(),
    },
    {
      key: 'templates',
      label: (
        <span>
          <CopyOutlined />
          {!isMobile && ' Plantillas'}
        </span>
      ),
      children: renderTemplatesTab(),
    },
  ];

  return (
    <div>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/configuracion')}
        style={{ padding: 0, marginBottom: 16 }}
      >
        Volver a Configuracion
      </Button>

      <Title level={2}>Menu del Dia</Title>

      {menuDelDiaSetting && !menuDelDiaSetting.value && (
        <Card
          style={{ marginBottom: 16, background: 'var(--bg-warning-light)', borderColor: 'var(--border-warning)' }}
          size="small"
        >
          <Text type="warning">
            La funcion de Menu del Dia esta desactivada. Activala desde Configuracion {'>'} Ajustes del Sistema para que aparezca en el punto de venta.
          </Text>
        </Card>
      )}

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size={isMobile ? 'small' : 'large'}
      />

      {/* Create/Edit Menu Modal */}
      <Modal
        title={editingMenu ? 'Editar Menu del Dia' : 'Crear Menu del Dia'}
        open={isMenuModalOpen}
        onOk={handleMenuModalOk}
        onCancel={() => {
          setIsMenuModalOpen(false);
          menuForm.resetFields();
          setMenuCourses([]);
        }}
        confirmLoading={creating || updating}
        width={isMobile ? '95%' : 800}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        <Form form={menuForm} layout="vertical">
          {!editingMenu && templates.length > 0 && (
            <Form.Item name="templateId" label="Plantilla (opcional)">
              <Select
                placeholder="Seleccionar plantilla para pre-llenar"
                allowClear
                onChange={(value) => value && handleTemplateSelect(value)}
                options={templates
                  .filter((t) => t.isActive)
                  .map((t) => ({
                    label: `${t.name} (${MEAL_TYPE_LABELS[t.mealType]})`,
                    value: t.id,
                  }))}
              />
            </Form.Item>
          )}
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="name"
                label="Nombre"
                rules={[{ required: true, message: 'Ingrese el nombre del menu' }]}
              >
                <Input placeholder="Ej: Menu Ejecutivo" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Item
                name="mealType"
                label="Tipo de comida"
                rules={[{ required: true, message: 'Seleccione el tipo' }]}
              >
                <Select
                  options={MEAL_TYPE_OPTIONS}
                  disabled={!!editingMenu}
                />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Item
                name="price"
                label={`Precio (${currencySymbol})`}
                rules={[{ required: true, message: 'Ingrese el precio' }]}
              >
                <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Descripcion">
            <TextArea rows={2} placeholder="Descripcion opcional" />
          </Form.Item>
          {editingMenu && (
            <Form.Item name="isActive" label="Activo" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}

          <Divider>Tiempos / Cursos</Divider>

          {cheapestComboIsFree && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={`La combinación más barata de este menú da ${currencySymbol}0`}
              description="Un comensal que elija esas opciones se cobraría en cero. Revisa el precio del menú o el de los platillos antes de guardar."
            />
          )}

          <CourseEditor
            courses={menuCourses}
            onChange={setMenuCourses}
            productionCenters={pcOptions}
            isMobile={isMobile}
            dishSuggestions={dishSuggestions}
            menuPrice={watchedMenuPrice}
          />
        </Form>
      </Modal>

      {/* Copy to Date Modal */}
      <Modal
        title="Copiar Menu a Otra Fecha"
        open={isCopyModalOpen}
        onOk={handleCopyConfirm}
        onCancel={() => setIsCopyModalOpen(false)}
        confirmLoading={copying}
        okButtonProps={{ disabled: !copyTargetDate }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>Selecciona la fecha destino para copiar este menu:</Text>
          <DatePicker
            value={copyTargetDate}
            onChange={(date) => setCopyTargetDate(date)}
            format="DD/MM/YYYY"
            style={{ width: '100%' }}
            disabledDate={(current) => current && current.isBefore(dayjs(), 'day')}
          />
        </Space>
      </Modal>

      {/* Template Modal */}
      <Modal
        title={editingTemplate ? 'Editar Plantilla' : 'Crear Plantilla'}
        open={isTemplateModalOpen}
        onOk={handleTemplateModalOk}
        onCancel={() => {
          setIsTemplateModalOpen(false);
          templateForm.resetFields();
          setTemplateCourses([]);
        }}
        confirmLoading={creatingTemplate || updatingTemplate}
        width={isMobile ? '95%' : 700}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        <Form form={templateForm} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="name"
                label="Nombre"
                rules={[{ required: true, message: 'Ingrese el nombre' }]}
              >
                <Input placeholder="Ej: Plantilla Menu Ejecutivo" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Item
                name="mealType"
                label="Tipo de comida"
                rules={[{ required: true, message: 'Seleccione' }]}
              >
                <Select options={MEAL_TYPE_OPTIONS} disabled={!!editingTemplate} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Item
                name="defaultPrice"
                label={`Precio base (${currencySymbol})`}
                rules={[{ required: true, message: 'Ingrese precio' }]}
              >
                <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Descripcion">
            <TextArea rows={2} />
          </Form.Item>
          {editingTemplate && (
            <Form.Item name="isActive" label="Activa" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}

          <Divider>Tiempos de la Plantilla</Divider>

          <TemplateCourseEditor
            courses={templateCourses}
            onChange={setTemplateCourses}
            productionCenters={pcOptions}
            isMobile={isMobile}
          />
        </Form>
      </Modal>
    </div>
  );
};

export default DailyMenuAdmin;
